import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { CurrentValuationSnapshot, TWRChart, PerformanceResult } from "@/schemas";
import {
  formatPercent,
  formatCompactCurrency,
  formatCurrency,
} from "../utils/format-utils";

interface PortfolioKeyMetricsProps {
  maxTwrData: TWRChart | null;
  performanceData: PerformanceResult | null;
  currentValuation: CurrentValuationSnapshot | null;
  currencyCode: string;
  variant?: "default" | "cash";
  loading: {
    twr: boolean;
    performance: boolean;
  };
}

interface MetricCardProps {
  label: string;
  value: string;
  isPositive?: boolean | null;
  colorize?: boolean;
}

function MetricCard({
  label,
  value,
  isPositive,
  colorize = false,
}: MetricCardProps) {
  const textColor =
    colorize && isPositive !== null
      ? isPositive
        ? "text-emerald-600"
        : "text-red-600"
      : "text-foreground";

  return (
    <div className="flex flex-col gap-1 p-2.5 sm:p-3 md:p-4 rounded-xl bg-muted/30 border border-border/20">
      <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "text-lg sm:text-2xl md:text-3xl font-bold tracking-tight",
          textColor,
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function PortfolioKeyMetrics({
  maxTwrData,
  performanceData,
  currentValuation,
  currencyCode,
  variant = "default",
  loading,
}: PortfolioKeyMetricsProps) {
  // Calculate metrics
  const metrics = useMemo(() => {
    const cagr = maxTwrData?.annualized_return_since_inception ?? null;

    const currentValue =
      currentValuation?.total_current_value ??
      performanceData?.data?.total_performance?.total_current_value ??
      0;
    const totalInvestedRaw =
      performanceData?.data?.total_performance?.total_cost ?? 0;
    const totalInvestedFromAssets = performanceData?.data?.assets_performance
      ? Object.values(performanceData.data.assets_performance).reduce(
          (sum, asset) => sum + Math.max(asset.total_cost ?? 0, 0),
          0,
        )
      : 0;
    const totalInvested =
      totalInvestedRaw > 0 ? totalInvestedRaw : totalInvestedFromAssets;

    // Total Gain/Loss all-time = Current Value - Total Invested
    const totalGainLoss = currentValue - totalInvested;

    // Sum dividends from all assets
    const totalDividends = performanceData?.data?.assets_performance
      ? Object.values(performanceData.data.assets_performance).reduce(
          (sum, asset) => sum + (asset.dividends_received ?? 0),
          0,
        )
      : 0;

    return {
      cagr,
      currentValue,
      totalInvested,
      totalGainLoss,
      totalDividends,
    };
  }, [maxTwrData, performanceData, currentValuation]);

  const isLoading = loading.twr || loading.performance;

  return (
    <div className="w-full rounded-2xl border border-border/40 bg-card shadow-sm p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base sm:text-xl font-bold tracking-tight">
          {variant === "cash" ? "Cash Account Overview" : "Portfolio Overview"}
        </h3>
        <Badge
          variant="secondary"
          className="text-[9px] font-bold uppercase tracking-widest"
        >
          All-Time
        </Badge>
      </div>

      {/* Metrics Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {/* CAGR Since Inception */}
          <MetricCard
            label={variant === "cash" ? "Annualized Return" : "CAGR /yr (since start)"}
            value={metrics.cagr !== null ? formatPercent(metrics.cagr) : "N/A"}
            isPositive={metrics.cagr !== null ? metrics.cagr >= 0 : null}
            colorize={metrics.cagr !== null}
          />

          {/* Current Portfolio Value */}
          <MetricCard
            label="Current Value"
            value={formatCompactCurrency(metrics.currentValue, currencyCode)}
          />

          {/* Total Invested */}
          <MetricCard
            label={variant === "cash" ? "Net Deposits" : "Total Invested"}
            value={formatCompactCurrency(metrics.totalInvested, currencyCode)}
          />

          {/* Total Gain/Loss */}
          <MetricCard
            label={variant === "cash" ? "FX Gain/Loss" : "Total Gain/Loss"}
            value={formatCurrency(metrics.totalGainLoss, currencyCode)}
            isPositive={metrics.totalGainLoss >= 0}
            colorize={true}
          />

          {/* Total Dividends */}
          <MetricCard
            label={variant === "cash" ? "Income Received" : "Dividends Received"}
            value={formatCurrency(metrics.totalDividends, currencyCode)}
          />
        </div>
      )}
    </div>
  );
}
